import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, ChatCircleDots, WhatsappLogo, PaperPlaneTilt, Info } from "@phosphor-icons/react";

/** Brevo settings — SMS + WhatsApp provider running alongside Twilio.
 *  API key is stored server-side (never exposed to the client) and only a
 *  preview like "ek_1234…abcd" is returned by GET. Empty key = no update.
 */
export default function BrevoPanel() {
  const [settings, setSettings] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [smsSender, setSmsSender] = useState("");
  const [whatsappSender, setWhatsappSender] = useState("");
  const [providerPref, setProviderPref] = useState("brevo_first");
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [waTesting, setWaTesting] = useState(false);

  const load = () => {
    api.get("/admin/brevo-settings").then((r) => {
      setSettings(r.data);
      setSmsSender(r.data.sms_sender || "");
      setWhatsappSender(r.data.whatsapp_sender || "");
      setProviderPref(r.data.provider_pref || "brevo_first");
      setWhatsappEnabled(!!r.data.whatsapp_enabled);
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sms_sender: smsSender || null,
        whatsapp_sender: whatsappSender || null,
        provider_pref: providerPref,
        whatsapp_enabled: whatsappEnabled,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const r = await api.patch("/admin/brevo-settings", payload);
      setSettings(r.data);
      setApiKey("");
      toast.success("Réglages Brevo sauvegardés");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const testSms = async () => {
    if (!testPhone.trim()) return toast.error("Numéro requis (+33...)");
    setTesting(true);
    try {
      const r = await api.post("/admin/brevo-settings/test-sms", { to: testPhone.trim() });
      toast.success(`SMS Brevo envoyé — id ${r.data.message_id || "?"}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  const testWa = async () => {
    if (!testPhone.trim()) return toast.error("Numéro requis (+33...)");
    setWaTesting(true);
    try {
      const r = await api.post("/admin/brevo-settings/test-whatsapp", { to: testPhone.trim() });
      toast.success(`WhatsApp envoyé — id ${r.data.message_id || "?"}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setWaTesting(false); }
  };

  const configured = !!settings.api_key_preview;

  return (
    <div className="space-y-4" data-testid="brevo-panel">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/25 p-4 flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${configured ? "bg-emerald-500" : "bg-white/10"}`}>
          {configured ? <CheckCircle size={22} weight="fill" color="white" /> : <ChatCircleDots size={22} weight="duotone" className="text-white/70" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold">Brevo {configured ? "connecté" : "non configuré"}</div>
          <div className="text-xs text-white/60 truncate">
            {configured
              ? `Clé ${settings.api_key_preview} · Expéditeur SMS ${settings.sms_sender || "BKPRO"}`
              : "SMS + WhatsApp France via Brevo (moins cher que Twilio, facture en €)"}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div>
          <Label className="text-white/60 text-[10px] uppercase tracking-widest">Clé API Brevo</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={configured ? "Laisser vide pour conserver" : "xkeysib-…"}
            className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
            data-testid="brevo-api-key"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Expéditeur SMS (max 11 car.)</Label>
            <Input
              value={smsSender}
              onChange={(e) => setSmsSender(e.target.value)}
              placeholder="BKPRO"
              maxLength={11}
              className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
              data-testid="brevo-sms-sender"
            />
          </div>
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-widest">Provider préféré</Label>
            <Select value={providerPref} onValueChange={setProviderPref}>
              <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="brevo-provider-pref"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="brevo_first">Brevo → fallback Twilio</SelectItem>
                <SelectItem value="twilio_first">Twilio → fallback Brevo</SelectItem>
                <SelectItem value="brevo_only">Brevo uniquement (pas de fallback)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={16} weight="duotone" className="text-emerald-400" />
            <div className="flex-1">
              <div className="text-sm font-semibold">Activer WhatsApp Business</div>
              <div className="text-[10px] text-white/50">Messages libres uniquement dans une fenêtre 24h après un message entrant client. Sinon templates validés obligatoires.</div>
            </div>
            <Switch checked={whatsappEnabled} onCheckedChange={setWhatsappEnabled} data-testid="brevo-wa-enabled" />
          </div>
          {whatsappEnabled && (
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Numéro WhatsApp d&apos;envoi (E.164)</Label>
              <Input
                value={whatsappSender}
                onChange={(e) => setWhatsappSender(e.target.value)}
                placeholder="+33612345678"
                className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
                data-testid="brevo-wa-sender"
              />
            </div>
          )}
        </div>

        <Button onClick={save} disabled={saving} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-2xl" data-testid="brevo-save">
          {saving ? "…" : "Enregistrer"}
        </Button>
      </div>

      <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-4 space-y-3" data-testid="brevo-test-panel">
        <div className="flex items-center gap-2 text-white/70 text-sm">
          <PaperPlaneTilt size={16} weight="duotone" /> <b>Tester la config</b>
        </div>
        <Input
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
          placeholder="+33612345678"
          className="bg-white/5 border-white/10 font-mono text-sm"
          data-testid="brevo-test-phone"
        />
        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={testSms}
            disabled={testing || !configured}
            className="bg-[#005CFF] hover:bg-[#0047CC] h-11 rounded-xl"
            data-testid="brevo-test-sms-btn"
          >
            <ChatCircleDots size={14} /> {testing ? "…" : "SMS de test"}
          </Button>
          <Button
            onClick={testWa}
            disabled={waTesting || !configured || !whatsappEnabled}
            className="bg-emerald-600 hover:bg-emerald-700 h-11 rounded-xl"
            data-testid="brevo-test-wa-btn"
          >
            <WhatsappLogo size={14} weight="fill" /> {waTesting ? "…" : "WhatsApp de test"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-[#005CFF]/10 border border-[#005CFF]/25 p-4 text-xs text-white/70 space-y-2">
        <div className="flex items-center gap-2 text-white font-semibold">
          <Info size={14} weight="duotone" /> Comment obtenir ta clé
        </div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Crée un compte gratuit sur <span className="text-[#7CB2FF]">brevo.com</span> (plan gratuit dispo).</li>
          <li>Va dans <b>Paramètres → SMTP & API → Clés API</b> → <b>Générer une nouvelle clé API</b>.</li>
          <li>Copie-la immédiatement (elle n&apos;apparaît qu&apos;une seule fois).</li>
          <li>Pour SMS : achète des crédits SMS (~0,078 €/SMS France).</li>
          <li>Pour WhatsApp : active WhatsApp Business dans <b>Apps &amp; Intégrations</b>, connecte ton compte Meta Business.</li>
          <li>Enregistre l&apos;expéditeur alphanumérique <b>BKPRO</b> pour la France (validation ~24-48h).</li>
        </ol>
      </div>
    </div>
  );
}
